import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  MarkOrganizationAsDSOReq,
  MarkOrganizationAsDSOResp,
  SetOrganizationDSOParentReq,
  SetOrganizationDSOParentResp,
  SetOrganizationFeatureFlagReq,
  SetOrganizationFeatureFlagResp,
  MarkOrganizationAsDistributorReq,
  MarkOrganizationAsDistributorResp,
  UnmarkOrganizationAsDistributorReq,
  UnmarkOrganizationAsDistributorResp,
  UnmarkOrganizationAsDSOReq,
  UnmarkOrganizationAsDSOResp,
  SetOrganizationDistributorReq,
  SetOrganizationDistributorResp,
  SystemLoad_GetReq,
  SystemLoad_GetResp,
  SystemLoad_SetSettingsReq,
  SystemLoad_SetSettingsResp,
} from 'protocol/api/core/svc_adminka_pb';
import {
  CreateOrganizationReq,
  CreateOrganizationResp,
} from 'protocol/api/core/svc_organization_pb';
import { SetUserNameReq, SetUserNameResp } from 'protocol/api/core/svc_user_pb';


import api, {
  AdminService,
  OrganizationService,
  UserService,
} from '@/shared/api/api';
import { SliceName } from '@/shared/config';
import { createThunkGenerator } from '@/shared/lib';

import { UserLocalSettings } from '../lib/getInitialUserSettings';


type AdminRequest = keyof typeof AdminService['methods'];
type OrganizationRequests = keyof typeof OrganizationService['methods'];
type UserRequests = keyof typeof UserService['methods'];
// TODO: think about 3 services in one slice
// It seems to me that there is no point in spreading it across different slices since these
// are just one queries that contextually relate to the user
const generateAdminThunk = createThunkGenerator<AdminRequest>(SliceName.user);
const generateOrganizationThunk = createThunkGenerator<OrganizationRequests>(
  SliceName.user,
);
const generateUserThunk = createThunkGenerator<UserRequests>(SliceName.user);

export const setSettings = createAsyncThunk(
  'user/setSettings',
  async (settings: Partial<UserLocalSettings>) => {
    Object.entries(settings).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });

    return settings;
  },
);

export const setOrganizationFeatureFlag = generateAdminThunk<SetOrganizationFeatureFlagReq, SetOrganizationFeatureFlagResp>(
  'setOrganizationFeatureFlag',
  api.core.admin.setOrganizationFeatureFlag,
);

export const createOrganization = generateOrganizationThunk<
  CreateOrganizationReq,
  CreateOrganizationResp
>('createOrganization', api.organization.createOrganization);

export const setOrganizationDSOParent = generateAdminThunk<
  SetOrganizationDSOParentReq,
  SetOrganizationDSOParentResp
>('setOrganizationDSOParent', api.core.admin.setOrganizationDSOParent);

export const markOrganizationAsDSO = generateAdminThunk<
  MarkOrganizationAsDSOReq,
  MarkOrganizationAsDSOResp
>('markOrganizationAsDSO', api.core.admin.markOrganizationAsDSO);

export const unmarkOrganizationAsDSO = generateAdminThunk<
  UnmarkOrganizationAsDSOReq,
  UnmarkOrganizationAsDSOResp
>('unmarkOrganizationAsDSO', api.core.admin.unmarkOrganizationAsDSO);

export const setUserName = generateUserThunk<SetUserNameReq, SetUserNameResp>(
  'setUserName',
  api.core.user.setUserName,
);

export const markOrganizationAsDistributor = generateAdminThunk<
  MarkOrganizationAsDistributorReq,
  MarkOrganizationAsDistributorResp
>('markOrganizationAsDistributor', api.core.admin.markOrganizationAsDistributor);

export const unmarkOrganizationAsDistributor = generateAdminThunk<
  UnmarkOrganizationAsDistributorReq,
  UnmarkOrganizationAsDistributorResp
>(
  'unmarkOrganizationAsDistributor',
  api.core.admin.unmarkOrganizationAsDistributor,
);

export const setOrganizationDistributor = generateAdminThunk<
  SetOrganizationDistributorReq,
  SetOrganizationDistributorResp
>(
  'setOrganizationDistributor',
  api.core.admin.setOrganizationDistributor,
);

export const getSystemLoad = generateAdminThunk<
  SystemLoad_GetReq,
  SystemLoad_GetResp
>('systemLoad_Get', api.core.admin.systemLoad_Get);

export const setSystemLoadSettings = generateAdminThunk<
  SystemLoad_SetSettingsReq,
  SystemLoad_SetSettingsResp
>('systemLoad_SetSettings', api.core.admin.systemLoad_SetSettings);
